/**
 * Program Catalog Data for Zodiac Workforce Intelligence Institute
 * Contains all program information organized by category
 */

export const PROGRAM_CATEGORIES = {
  "executive-professional-masters": {
    id: "executive-professional-masters",
    title: "Executive Professional Masters",
    shortTitle: "EMP",
    color: "#1E3A8A"
  },
  "training-certificates": {
    id: "training-certificates",
    title: "Training Certificates",
    shortTitle: "Certificate",
    color: "#2563EB"
  },
  "courses": {
    id: "courses",
    title: "Courses",
    shortTitle: "Course",
    color: "#3B82F6"
  },
  "licensing": {
    id: "licensing",
    title: "Licensing Programs",
    shortTitle: "License",
    color: "#60A5FA"
  }
};

export const PROGRAMS = {
  // ================================================
  // EXECUTIVE PROFESSIONAL MASTERS
  // ================================================
  "emp-leadership-tqm": {
    id: "emp-leadership-tqm",
    slug: "emp-leadership-tqm",
    category: "executive-professional-masters",
    title: "Leadership – Total Quality Management",
    shortDescription: "Master the integration of leadership excellence with total quality management principles for organizational transformation.",
    description: "This Executive Professional Masters program combines advanced leadership development with comprehensive Total Quality Management methodologies. Graduates become strategic leaders capable of driving continuous improvement, operational excellence, and quality-focused organizational cultures.",
    vision: "Develop visionary leaders who integrate quality management principles into strategic decision-making, fostering cultures of continuous improvement and operational excellence across all organizational levels.",
    objectives: [
      "Master TQM principles and their application in executive leadership contexts",
      "Develop strategic quality planning and deployment capabilities",
      "Lead organization-wide quality improvement initiatives",
      "Integrate quality metrics into executive decision-making",
      "Build sustainable quality management systems",
      "Foster a culture of continuous improvement and excellence"
    ],
    orientation: ["Leadership Development", "Operational Excellence", "Quality Systems", "Strategic Management"],
    structure: ["Applied learning modules", "Case study analysis", "AI-supported learning tools", "Industry scenario simulations", "Practical quality projects", "Executive capstone"],
    careerPathways: ["Chief Quality Officer", "VP of Operations", "Director of Continuous Improvement", "Quality Excellence Leader", "Senior Operations Executive"],
    duration: "12-18 months",
    format: "Hybrid (Online + Residencies)",
    tuition: "$35,000 - $45,000",
    curriculum: [
      { module: "Module 1", title: "Leadership Foundations & TQM Principles", description: "Core leadership competencies and TQM fundamentals" },
      { module: "Module 2", title: "Strategic Quality Planning", description: "Quality strategy development and deployment" },
      { module: "Module 3", title: "Process Excellence & Six Sigma", description: "Advanced process improvement methodologies" },
      { module: "Module 4", title: "Quality Systems & Standards", description: "ISO standards and quality management systems" },
      { module: "Module 5", title: "Applied Capstone Project", description: "Organization-wide quality transformation initiative" }
    ]
  },

  "emp-ehsq": {
    id: "emp-ehsq",
    slug: "emp-ehsq",
    category: "executive-professional-masters",
    title: "Leadership – Environment, Health, Safety & Quality",
    shortDescription: "Lead integrated EHSQ management systems that protect people, environment, and organizational performance.",
    description: "The EHSQ Executive Professional Masters prepares leaders to manage comprehensive Environment, Health, Safety, and Quality programs. This program integrates regulatory compliance, risk management, and operational excellence into a unified leadership framework.",
    vision: "Create executive leaders who champion integrated EHSQ management, driving sustainable business practices while ensuring workforce safety, environmental stewardship, and quality excellence.",
    objectives: [
      "Lead enterprise-wide EHSQ strategy and implementation",
      "Master regulatory compliance across environmental, health, and safety domains",
      "Integrate quality management with safety and environmental systems",
      "Develop risk assessment and mitigation capabilities",
      "Build cultures of safety excellence and environmental responsibility",
      "Drive sustainability initiatives aligned with business objectives"
    ],
    orientation: ["Safety & Compliance", "Environmental Management", "Quality Systems", "Risk Management"],
    structure: ["Executive seminars", "Regulatory case studies", "AI-supported compliance tools", "Field assessments", "Capstone implementation project"],
    careerPathways: ["Chief Safety Officer", "VP of EHSQ", "Director of Environmental Compliance", "Corporate Safety Director", "Sustainability Executive"],
    duration: "12-18 months",
    format: "Hybrid (Online + Residencies)",
    tuition: "$35,000 - $45,000",
    curriculum: [
      { module: "Module 1", title: "EHSQ Leadership Foundations", description: "Integrated EHSQ leadership principles" },
      { module: "Module 2", title: "Environmental Management Systems", description: "ISO 14001 and environmental compliance" },
      { module: "Module 3", title: "Occupational Health & Safety", description: "Safety management and OSHA compliance" },
      { module: "Module 4", title: "Quality Integration & Risk Management", description: "Unified management systems approach" },
      { module: "Module 5", title: "Applied Capstone Project", description: "EHSQ transformation initiative" }
    ]
  },

  "emp-project-management": {
    id: "emp-project-management",
    slug: "emp-project-management",
    category: "executive-professional-masters",
    title: "Leadership – Project Management",
    shortDescription: "Excel in strategic project leadership, portfolio management, and organizational project governance.",
    description: "This program develops executive-level project management capabilities, preparing leaders to oversee complex project portfolios, establish governance frameworks, and drive strategic initiatives across organizations.",
    vision: "Develop strategic project leaders who align project portfolios with organizational strategy, optimizing resource allocation and delivering transformational business outcomes.",
    objectives: [
      "Master strategic project portfolio management",
      "Establish enterprise project governance frameworks",
      "Lead complex, multi-stakeholder project initiatives",
      "Integrate AI tools into project management practices",
      "Develop project leadership and team development skills",
      "Optimize project resource allocation and risk management"
    ],
    orientation: ["Strategic Management", "Project Leadership", "Portfolio Governance", "Resource Optimization"],
    structure: ["Executive project simulations", "Portfolio case studies", "AI project management tools", "Stakeholder scenarios", "Capstone portfolio project"],
    careerPathways: ["Chief Project Officer", "VP of Program Management", "Director of Strategic Initiatives", "Enterprise PMO Leader", "Portfolio Management Executive"],
    duration: "12-18 months",
    format: "Hybrid (Online + Residencies)",
    tuition: "$35,000 - $45,000",
    curriculum: [
      { module: "Module 1", title: "Strategic Project Leadership", description: "Executive project leadership foundations" },
      { module: "Module 2", title: "Portfolio Management Excellence", description: "Portfolio optimization and governance" },
      { module: "Module 3", title: "Advanced Project Methodologies", description: "Agile, hybrid, and traditional approaches" },
      { module: "Module 4", title: "Project Risk & Stakeholder Management", description: "Risk mitigation and stakeholder engagement" },
      { module: "Module 5", title: "Applied Capstone Project", description: "Strategic project portfolio initiative" }
    ]
  },

  "emp-workforce-intelligence": {
    id: "emp-workforce-intelligence",
    slug: "emp-workforce-intelligence",
    category: "executive-professional-masters",
    title: "Workforce Intelligence Practitioner",
    shortDescription: "Become a certified practitioner in workforce intelligence methodologies and AI-driven workforce transformation.",
    description: "The flagship Workforce Intelligence Practitioner program provides comprehensive training in Zodiac's proprietary workforce intelligence frameworks, diagnostic tools, and transformation methodologies for the AI era.",
    vision: "Develop certified practitioners who can assess, diagnose, and transform workforce capabilities using advanced analytics, AI integration, and evidence-based methodologies.",
    objectives: [
      "Master the Workforce Intelligence Index (WII) framework",
      "Conduct comprehensive workforce diagnostic assessments",
      "Design AI-powered workforce transformation strategies",
      "Implement workforce readiness and capability programs",
      "Develop predictive workforce analytics capabilities",
      "Lead organizational workforce intelligence initiatives"
    ],
    orientation: ["Workforce Intelligence", "AI Integration", "Organizational Development", "Analytics & Diagnostics"],
    structure: ["Practitioner certification modules", "Diagnostic tool training", "AI workforce platforms", "Applied assessments", "Certification capstone"],
    careerPathways: ["Chief Workforce Officer", "Workforce Intelligence Director", "HR Transformation Leader", "People Analytics Executive", "Organizational Development VP"],
    duration: "12-18 months",
    format: "Hybrid (Online + Residencies)",
    tuition: "$40,000 - $45,000",
    curriculum: [
      { module: "Module 1", title: "Workforce Intelligence Foundations", description: "WII framework and diagnostic principles" },
      { module: "Module 2", title: "AI & Workforce Transformation", description: "AI integration strategies and tools" },
      { module: "Module 3", title: "Workforce Analytics & Diagnostics", description: "Assessment methodologies and analytics" },
      { module: "Module 4", title: "Organizational Development", description: "Change management and capability building" },
      { module: "Module 5", title: "Practitioner Certification Capstone", description: "Applied workforce transformation project" }
    ]
  },

  "emp-six-sigma-master-black-belt": {
    id: "emp-six-sigma-master-black-belt",
    slug: "emp-six-sigma-master-black-belt",
    category: "executive-professional-masters",
    title: "Leadership – Six Sigma Master Black Belt",
    shortDescription: "Achieve the highest level of Six Sigma expertise with executive leadership integration.",
    description: "This elite program combines Six Sigma Master Black Belt certification with executive leadership development. Graduates become organizational change agents capable of leading enterprise-wide quality and process excellence initiatives.",
    vision: "Develop Master Black Belt practitioners who lead organizational transformation through statistical rigor, process excellence, and strategic quality leadership.",
    objectives: [
      "Achieve Six Sigma Master Black Belt certification",
      "Lead enterprise-wide process improvement programs",
      "Mentor and develop Black Belt and Green Belt practitioners",
      "Integrate statistical methods with strategic decision-making",
      "Design and deploy continuous improvement cultures",
      "Drive measurable business results through Six Sigma"
    ],
    orientation: ["Operational Excellence", "Statistical Analysis", "Process Improvement", "Change Leadership"],
    structure: ["Advanced statistical training", "MBB project leadership", "Coaching and mentoring practicum", "Enterprise deployment strategies", "Certification capstone"],
    careerPathways: ["Master Black Belt Leader", "VP of Operational Excellence", "Director of Continuous Improvement", "Chief Quality Officer", "Process Excellence Executive"],
    duration: "18 months",
    format: "Hybrid (Online + Residencies)",
    tuition: "$40,000 - $45,000",
    curriculum: [
      { module: "Module 1", title: "Advanced Statistical Methods", description: "Multivariate analysis and DOE" },
      { module: "Module 2", title: "Enterprise Deployment Leadership", description: "Organization-wide Six Sigma strategy" },
      { module: "Module 3", title: "Coaching & Mentoring Excellence", description: "Developing Black Belt practitioners" },
      { module: "Module 4", title: "Strategic Quality Leadership", description: "Executive quality governance" },
      { module: "Module 5", title: "Master Black Belt Capstone", description: "Enterprise transformation project" }
    ]
  },

  // ================================================
  // TRAINING CERTIFICATES
  // ================================================
  "cert-osha": {
    id: "cert-osha",
    slug: "cert-osha",
    category: "training-certificates",
    title: "OSHA Safety Certification",
    shortDescription: "Comprehensive OSHA compliance training for workplace safety professionals.",
    description: "This certification program provides thorough training in OSHA regulations, workplace safety standards, and compliance requirements. Participants learn to identify hazards, implement safety programs, and maintain regulatory compliance.",
    vision: "Create safety professionals who protect workers and organizations through comprehensive OSHA compliance and proactive safety management.",
    objectives: [
      "Master OSHA regulatory requirements and standards",
      "Conduct workplace hazard assessments",
      "Develop and implement safety programs",
      "Lead safety training and awareness initiatives",
      "Manage OSHA inspections and documentation"
    ],
    orientation: ["Safety & Compliance", "Risk Assessment", "Regulatory Knowledge"],
    duration: "3-4 months",
    format: "Online / Blended",
    tuition: "$3,500 - $5,500"
  },

  "cert-iso-9000": {
    id: "cert-iso-9000",
    slug: "cert-iso-9000",
    category: "training-certificates",
    title: "ISO 9000 Quality Management",
    shortDescription: "ISO 9000 series certification for quality management system implementation and auditing.",
    description: "This certificate program covers ISO 9001 quality management system requirements, implementation strategies, and internal auditing techniques. Participants gain skills to establish and maintain QMS certification.",
    vision: "Develop quality professionals capable of implementing and maintaining world-class quality management systems aligned with ISO 9001 standards.",
    objectives: [
      "Understand ISO 9001:2015 requirements",
      "Implement quality management systems",
      "Conduct internal quality audits",
      "Drive continuous improvement initiatives",
      "Prepare organizations for certification"
    ],
    orientation: ["Quality Systems", "Process Improvement", "Auditing"],
    duration: "3-4 months",
    format: "Online / Blended",
    tuition: "$3,500 - $5,500"
  },

  "cert-iso-14000": {
    id: "cert-iso-14000",
    slug: "cert-iso-14000",
    category: "training-certificates",
    title: "ISO 14000 Environmental Management",
    shortDescription: "Environmental management system certification based on ISO 14001 standards.",
    description: "This program provides comprehensive training in ISO 14001 environmental management systems, including implementation, auditing, and continuous improvement of environmental performance.",
    vision: "Create environmental management professionals who drive sustainable business practices and environmental stewardship through systematic EMS implementation.",
    objectives: [
      "Master ISO 14001:2015 requirements",
      "Implement environmental management systems",
      "Conduct environmental aspects assessment",
      "Lead environmental audits",
      "Drive environmental performance improvement"
    ],
    orientation: ["Environmental Management", "Sustainability", "Compliance"],
    duration: "3-4 months",
    format: "Online / Blended",
    tuition: "$3,500 - $5,500"
  },

  "cert-iso-8000": {
    id: "cert-iso-8000",
    slug: "cert-iso-8000",
    category: "training-certificates",
    title: "ISO 8000 Data Quality",
    shortDescription: "Data quality management certification for information excellence.",
    description: "This certificate focuses on ISO 8000 data quality standards, teaching participants to establish data governance, quality metrics, and continuous data improvement programs.",
    vision: "Develop data quality professionals who ensure organizational data assets meet the highest standards of accuracy, completeness, and reliability.",
    objectives: [
      "Understand ISO 8000 data quality framework",
      "Implement data quality management programs",
      "Establish data governance structures",
      "Measure and improve data quality metrics",
      "Lead data quality initiatives"
    ],
    orientation: ["Data Management", "Quality Systems", "Information Governance"],
    duration: "3 months",
    format: "Online",
    tuition: "$2,500 - $4,000"
  },

  "cert-leadership-effectiveness": {
    id: "cert-leadership-effectiveness",
    slug: "cert-leadership-effectiveness",
    category: "training-certificates",
    title: "Applied Leadership Effectiveness",
    shortDescription: "Practical leadership skills for emerging and mid-level leaders.",
    description: "This certificate program develops core leadership competencies including communication, team development, decision-making, and performance management through applied learning.",
    vision: "Develop effective leaders who inspire teams, drive results, and create positive organizational cultures.",
    objectives: [
      "Master essential leadership communication skills",
      "Develop high-performing teams",
      "Make effective decisions under pressure",
      "Manage performance and provide feedback",
      "Lead through change and uncertainty"
    ],
    orientation: ["Leadership Development", "Team Management", "Communication"],
    duration: "4-6 months",
    format: "Online / Blended",
    tuition: "$4,500 - $6,500"
  },

  "cert-security-service-quality": {
    id: "cert-security-service-quality",
    slug: "cert-security-service-quality",
    category: "training-certificates",
    title: "Security and Service Quality",
    shortDescription: "Professional certification in security operations and service excellence.",
    description: "This program combines security operations training with service quality principles, preparing professionals to deliver exceptional security services while maintaining customer satisfaction.",
    vision: "Create security professionals who balance operational excellence with superior service delivery and client satisfaction.",
    objectives: [
      "Master security operations principles",
      "Develop service quality skills",
      "Manage client relationships effectively",
      "Implement quality assurance programs",
      "Lead security service teams"
    ],
    orientation: ["Security Operations", "Service Excellence", "Quality Management"],
    duration: "3-4 months",
    format: "Online / Blended",
    tuition: "$3,000 - $5,000"
  },

  "cert-private-investigation": {
    id: "cert-private-investigation",
    slug: "cert-private-investigation",
    category: "training-certificates",
    title: "Private Investigation",
    shortDescription: "Professional certification for private investigation and intelligence gathering.",
    description: "This comprehensive program covers investigation techniques, legal requirements, evidence handling, and reporting standards for aspiring and practicing private investigators.",
    vision: "Develop professional investigators who conduct ethical, thorough, and legally compliant investigations.",
    objectives: [
      "Master investigation methodologies",
      "Understand legal and ethical requirements",
      "Develop surveillance and research skills",
      "Handle and document evidence properly",
      "Prepare professional investigation reports"
    ],
    orientation: ["Investigation Techniques", "Legal Compliance", "Evidence Management"],
    duration: "4-6 months",
    format: "Blended",
    tuition: "$5,500 - $8,500"
  },

  // ================================================
  // COURSES
  // ================================================
  "course-construction": {
    id: "course-construction",
    slug: "course-construction",
    category: "courses",
    title: "Construction Fundamentals",
    shortDescription: "Essential construction knowledge for industry professionals.",
    description: "This course covers construction fundamentals including project planning, safety protocols, materials management, and quality standards for construction industry professionals.",
    orientation: ["Construction Management", "Safety", "Project Planning"],
    duration: "8-12 weeks",
    format: "Online / Self-Paced",
    tuition: "$995 - $1,495"
  },

  "course-logistics": {
    id: "course-logistics",
    slug: "course-logistics",
    category: "courses",
    title: "Logistics Management",
    shortDescription: "Comprehensive logistics and supply chain operations training.",
    description: "Learn logistics fundamentals including transportation management, inventory control, distribution operations, and supply chain optimization strategies.",
    orientation: ["Supply Chain", "Operations", "Transportation"],
    duration: "6-8 weeks",
    format: "Online / Self-Paced",
    tuition: "$795 - $1,195"
  },

  "course-warehouse-operations": {
    id: "course-warehouse-operations",
    slug: "course-warehouse-operations",
    category: "courses",
    title: "Warehouse Operations",
    shortDescription: "Master warehouse management and distribution operations.",
    description: "This course covers warehouse management systems, inventory control, picking and packing operations, safety protocols, and efficiency optimization.",
    orientation: ["Warehouse Management", "Inventory Control", "Operations"],
    duration: "4-6 weeks",
    format: "Online / Self-Paced",
    tuition: "$595 - $895"
  },

  "course-quality-inspection": {
    id: "course-quality-inspection",
    slug: "course-quality-inspection",
    category: "courses",
    title: "Quality Inspection",
    shortDescription: "Quality inspection techniques and standards.",
    description: "Learn quality inspection methodologies, measurement techniques, defect identification, and quality documentation standards.",
    orientation: ["Quality Control", "Inspection Techniques", "Standards"],
    duration: "4-6 weeks",
    format: "Online / Self-Paced",
    tuition: "$595 - $895"
  },

  "course-quality-auditing": {
    id: "course-quality-auditing",
    slug: "course-quality-auditing",
    category: "courses",
    title: "Quality Auditing",
    shortDescription: "Internal and external quality auditing skills.",
    description: "Develop auditing competencies including audit planning, execution, reporting, and corrective action management for quality management systems.",
    orientation: ["Auditing", "Quality Systems", "Compliance"],
    duration: "6-8 weeks",
    format: "Online / Self-Paced",
    tuition: "$895 - $1,295"
  },

  "course-six-sigma-yellow-belt": {
    id: "course-six-sigma-yellow-belt",
    slug: "course-six-sigma-yellow-belt",
    category: "courses",
    title: "Six Sigma Yellow Belt",
    shortDescription: "Foundation-level Six Sigma methodology training.",
    description: "This introductory course covers Six Sigma fundamentals, DMAIC methodology basics, and team member roles in improvement projects.",
    orientation: ["Process Improvement", "Six Sigma", "Quality"],
    duration: "4 weeks",
    format: "Online / Self-Paced",
    tuition: "$495 - $695"
  },

  "course-six-sigma-green-belt": {
    id: "course-six-sigma-green-belt",
    slug: "course-six-sigma-green-belt",
    category: "courses",
    title: "Six Sigma Green Belt",
    shortDescription: "Intermediate Six Sigma certification and project leadership.",
    description: "Comprehensive Green Belt training covering DMAIC methodology, statistical tools, project leadership, and improvement implementation.",
    orientation: ["Six Sigma", "Project Leadership", "Statistical Analysis"],
    duration: "8-10 weeks",
    format: "Online / Blended",
    tuition: "$1,495 - $1,995"
  },

  "course-six-sigma-black-belt": {
    id: "course-six-sigma-black-belt",
    slug: "course-six-sigma-black-belt",
    category: "courses",
    title: "Six Sigma Black Belt",
    shortDescription: "Advanced Six Sigma certification and organizational leadership.",
    description: "Advanced Black Belt training including complex statistical analysis, project portfolio management, coaching skills, and organizational deployment.",
    orientation: ["Six Sigma", "Advanced Statistics", "Change Leadership"],
    duration: "12 weeks",
    format: "Blended",
    tuition: "$1,795 - $1,995"
  },

  "course-lean-toc": {
    id: "course-lean-toc",
    slug: "course-lean-toc",
    category: "courses",
    title: "Lean Manufacturing & Theory of Constraints",
    shortDescription: "Integrated lean principles with constraint management.",
    description: "This course integrates Lean Manufacturing principles with Theory of Constraints, teaching participants to eliminate waste while optimizing system throughput.",
    orientation: ["Lean Manufacturing", "Theory of Constraints", "Operations"],
    duration: "8-10 weeks",
    format: "Online / Blended",
    tuition: "$1,295 - $1,695"
  },

  // ================================================
  // LICENSING PROGRAMS
  // ================================================
  "license-cdl": {
    id: "license-cdl",
    slug: "license-cdl",
    category: "licensing",
    title: "Commercial Driver License (CDL)",
    shortDescription: "Professional CDL training and licensing preparation.",
    description: "Comprehensive CDL training program covering vehicle operation, safety regulations, DOT requirements, and licensing examination preparation for Class A, B, and C licenses.",
    orientation: ["Vehicle Operations", "Safety Compliance", "DOT Regulations"],
    duration: "4-8 weeks",
    format: "In-Person",
    tuition: "Contact for pricing"
  },

  "license-security-professional": {
    id: "license-security-professional",
    slug: "license-security-professional",
    category: "licensing",
    title: "Security Professional (Level 2, 3, 4)",
    shortDescription: "State-certified security officer licensing at multiple levels.",
    description: "Professional security licensing program covering all certification levels from basic security officer (Level 2) through armed security (Level 4), including state requirements and examination preparation.",
    orientation: ["Security Operations", "Legal Requirements", "Professional Conduct"],
    duration: "2-6 weeks per level",
    format: "In-Person / Blended",
    tuition: "Contact for pricing"
  },

  "license-construction-equipment": {
    id: "license-construction-equipment",
    slug: "license-construction-equipment",
    category: "licensing",
    title: "Construction Equipment Operators",
    shortDescription: "Heavy equipment operation certification and licensing.",
    description: "Professional training for construction equipment operators including excavators, bulldozers, loaders, and cranes. Covers safety protocols, operation techniques, and certification requirements.",
    orientation: ["Equipment Operations", "Safety", "Construction"],
    duration: "4-12 weeks",
    format: "In-Person",
    tuition: "Contact for pricing"
  },

  "license-forklift": {
    id: "license-forklift",
    slug: "license-forklift",
    category: "licensing",
    title: "Forklift Driver / Operator",
    shortDescription: "OSHA-compliant forklift operator certification.",
    description: "OSHA-compliant forklift operator training covering sit-down, stand-up, and reach truck operations. Includes safety protocols, load handling, and certification testing.",
    orientation: ["Equipment Operations", "OSHA Compliance", "Warehouse Safety"],
    duration: "1-2 weeks",
    format: "In-Person",
    tuition: "Contact for pricing"
  }
};

// Helper function to get programs by category
export function getProgramsByCategory(categoryId) {
  return Object.values(PROGRAMS).filter(p => p.category === categoryId);
}

// Helper function to get all programs as array
export function getAllPrograms() {
  return Object.values(PROGRAMS);
}

// Helper function to get program by slug
export function getProgramBySlug(slug) {
  return PROGRAMS[slug] || null;
}
